import { useEffect, useState, useMemo } from "react";
import { api, STATUS_LABEL, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, PackageCheck, Trash2, ArrowDownToLine } from "lucide-react";
import AddWorkDialog from "@/components/AddWorkDialog";
import ReceiveWorkDialog from "@/components/ReceiveWorkDialog";
import { toast } from "sonner";

export default function Works() {
  const [works, setWorks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [workerFilter, setWorkerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [receiveWork, setReceiveWork] = useState(null);

  const load = async () => {
    try {
      const [wks, ws] = await Promise.all([
        api.get("/works"),
        api.get("/workers"),
      ]);
      setWorks(wks.data);
      setWorkers(ws.data);
    } catch {
      toast.error("Yüklenemedi");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return works.filter(w => {
      if (statusFilter !== "all" && w.status !== statusFilter) return false;
      if (workerFilter !== "all" && w.worker_id !== workerFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          w.work_type.toLowerCase().includes(q) ||
          w.worker_name.toLowerCase().includes(q) ||
          (w.description || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [works, statusFilter, workerFilter, search]);

  const deleteWork = async (id) => {
    if (!window.confirm("Bu işi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/works/${id}`);
      toast.success("İş silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-8">
      <section className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
        <div>
          <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#D98359]">İş Kayıtları</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#1A1D1A]">Tüm İşler</h1>
          <div className="brand-underline mt-3 w-16" />
        </div>
        <Button
          onClick={() => setOpenAdd(true)}
          className="rounded-full h-12 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white shadow-soft transition-colors"
          data-testid="add-work-btn-works"
        >
          <Plus size={18} className="mr-1.5" />
          Yeni İş Ekle
        </Button>
      </section>

      {/* Filters */}
      <Card className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-4 sm:p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C605C]" />
            <Input
              placeholder="Ara: iş türü, açıklama, çalışan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-12 pl-10 rounded-xl bg-white border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
              data-testid="works-search"
            />
          </div>
          <Select value={workerFilter} onValueChange={setWorkerFilter}>
            <SelectTrigger className="h-12 rounded-xl bg-white border-[#E6E6E2]" data-testid="filter-worker">
              <SelectValue placeholder="Çalışan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm çalışanlar</SelectItem>
              {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-12 rounded-xl bg-white border-[#E6E6E2]" data-testid="filter-status">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              <SelectItem value="in_progress">Devam Ediyor</SelectItem>
              <SelectItem value="completed">Tamamlandı</SelectItem>
              <SelectItem value="pending">Bekliyor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Works list */}
      {filtered.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-[#D6D6CE] bg-white p-12 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-[#F1F1EE] grid place-items-center mb-4">
            <PackageCheck size={22} className="text-[#5C605C]" />
          </div>
          <div className="font-display text-lg font-medium text-[#1A1D1A]">Henüz iş kaydı yok</div>
          <p className="text-sm text-[#5C605C] mt-1">Yukarıdan &ldquo;Yeni İş Ekle&rdquo; ile başlayabilirsiniz.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filtered.map((w, i) => (
            <Card
              key={w.id}
              className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-5 sm:p-6 card-hover hover:border-[#D6D6CE] fade-up"
              style={{ animationDelay: `${i * 30}ms` }}
              data-testid={`work-item-${w.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#3B5249] to-[#5C7D64] text-white grid place-items-center font-display font-semibold shrink-0">
                    {w.worker_name.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-[#1A1D1A]">{w.work_type}</span>
                      <span className={`status-pill status-${w.status}`}>{STATUS_LABEL[w.status]}</span>
                    </div>
                    <div className="text-sm text-[#5C605C] mt-0.5">
                      {w.worker_name} · Veriliş: {formatDate(w.given_date)}
                      {w.received_date && ` · Alınış: ${formatDate(w.received_date)}`}
                    </div>
                    {w.description && (
                      <div className="text-sm text-[#3D403D] mt-2">{w.description}</div>
                    )}
                    {w.notes && (
                      <div className="text-xs text-[#5C605C] mt-2 italic">Not: {w.notes}</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-6 sm:gap-8 sm:ml-4 sm:shrink-0">
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Verilen</div>
                    <div className="font-display font-semibold text-lg text-[#3B5249]">{w.quantity_given}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Alınan</div>
                    <div className="font-display font-semibold text-lg text-[#5C7D64]">{w.quantity_received}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#5C605C]">Kalan</div>
                    <div className="font-display font-semibold text-lg text-[#A85F26]">{w.quantity_given - w.quantity_received}</div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-2 flex-wrap">
                {w.status !== "completed" && (
                  <Button
                    onClick={() => setReceiveWork(w)}
                    className="rounded-full h-10 px-5 bg-[#5C7D64] hover:bg-[#4A6752] text-white text-sm transition-colors"
                    data-testid={`receive-btn-${w.id}`}
                  >
                    <ArrowDownToLine size={14} className="mr-1.5" />
                    Teslim Al
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={() => deleteWork(w.id)}
                  className="rounded-full h-10 px-4 text-[#A85F26] hover:text-[#A85F26] hover:bg-[#FBEEDF] text-sm transition-colors"
                  data-testid={`delete-btn-${w.id}`}
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Sil
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddWorkDialog open={openAdd} onOpenChange={setOpenAdd} workers={workers} onSuccess={load} />
      <ReceiveWorkDialog work={receiveWork} onOpenChange={() => setReceiveWork(null)} onSuccess={load} />
    </div>
  );
}
